// =============================================================================
// PTEL - Editor Request Service
// =============================================================================
// Gestión de solicitudes de rol Editor.
// FB.10.03 · createRequest, listPendingRequests, approveRequest, rejectRequest
// =============================================================================

import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, isConfigured, COLLECTIONS } from '../../config/firebase';
import type { EditorRequest, RoleDuration } from '../../types/roles';
import { calculateExpirationDate } from '../../types/roles';
import { sendRequestResultNotification } from '../email/emailNotification.service';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Convierte Firestore Timestamp (o Date) a Date */
function toDate(value: unknown): Date {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return value instanceof Date ? value : new Date();
}

// -----------------------------------------------------------------------------
// Check Pending Request
// -----------------------------------------------------------------------------

/**
 * Consulta si el usuario tiene una solicitud pendiente.
 * @returns Fecha de solicitud o null si no hay pendiente
 */
export async function getUserPendingRequest(
  uid: string,
): Promise<{ requestedAt: Date } | null> {
  if (!isConfigured || !db) return null;

  const q = query(
    collection(db, COLLECTIONS.EDITOR_REQUESTS),
    where('uid', '==', uid),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  const data = snap.docs[0].data();
  const requestedAt = toDate(data.requestedAt);
  return { requestedAt };
}

// -----------------------------------------------------------------------------
// Create Request
// -----------------------------------------------------------------------------

/**
 * Crea una solicitud de rol Editor.
 * Valida que no exista otra solicitud pendiente del mismo usuario.
 * @returns ID del documento creado
 * @throws Si ya existe una solicitud pendiente o Firebase no está configurado
 */
export async function createRequest(
  userData: { uid: string; email: string; displayName: string },
  reason: string,
): Promise<string> {
  if (!isConfigured || !db) {
    throw new Error('Firebase no configurado');
  }

  // Validar máx. 1 pendiente por usuario
  const pendingQ = query(
    collection(db, COLLECTIONS.EDITOR_REQUESTS),
    where('uid', '==', userData.uid),
    where('status', '==', 'pending'),
  );
  const existing = await getDocs(pendingQ);
  if (!existing.empty) {
    throw new Error('Ya existe una solicitud pendiente');
  }

  const docRef = await addDoc(
    collection(db, COLLECTIONS.EDITOR_REQUESTS),
    {
      uid: userData.uid,
      email: userData.email,
      displayName: userData.displayName,
      reason: reason.slice(0, 500),
      requestedAt: serverTimestamp(),
      status: 'pending',
    },
  );

  return docRef.id;
}

// -----------------------------------------------------------------------------
// List Pending Requests (admin — protegido por rules)
// -----------------------------------------------------------------------------

export async function listPendingRequests(): Promise<EditorRequest[]> {
  if (!isConfigured || !db) return [];

  const q = query(
    collection(db, COLLECTIONS.EDITOR_REQUESTS),
    where('status', '==', 'pending'),
    orderBy('requestedAt', 'asc'),
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      uid: data.uid ?? '',
      email: data.email ?? '',
      displayName: data.displayName ?? '',
      reason: data.reason ?? '',
      requestedAt: toDate(data.requestedAt),
      status: data.status ?? 'pending',
    } satisfies EditorRequest;
  });
}

// -----------------------------------------------------------------------------
// Approve Request
// -----------------------------------------------------------------------------

/**
 * Aprueba una solicitud pendiente en un solo lote (SEC0-b2c DOP-6 a′):
 * solicitud → 'approved' y ficha → 'editor', o ninguna de las dos.
 * La ficha guarda `roleRequestId`, que las reglas usan para exigir la solicitud.
 * El correo de resultado sale solo si el lote se ha escrito.
 */
export async function approveRequest(
  requestId: string,
  adminUid: string,
  duration: RoleDuration,
): Promise<void> {
  if (!isConfigured || !db) {
    throw new Error('Firebase no configurado');
  }

  // Leer uid del solicitante desde la solicitud
  const requestRef = doc(db, COLLECTIONS.EDITOR_REQUESTS, requestId);
  const reqSnap = await getDoc(requestRef);

  if (!reqSnap.exists()) {
    throw new Error('Solicitud no encontrada');
  }

  if (reqSnap.data().status !== 'pending') {
    throw new Error('La solicitud ya está resuelta');
  }

  const requestUid = reqSnap.data().uid as string;
  const now = new Date();
  const expiresAt = calculateExpirationDate(duration, now);

  const batch = writeBatch(db);
  batch.update(requestRef, {
    status: 'approved',
    reviewedBy: adminUid,
    reviewedAt: serverTimestamp(),
    grantedDuration: duration,
  });
  batch.update(doc(db, COLLECTIONS.USERS, requestUid), {
    role: 'editor',
    roleGrantedAt: serverTimestamp(),
    roleExpiresAt: expiresAt,
    roleGrantedBy: adminUid,
    roleRequestId: requestId,
  });
  await batch.commit();

  // Notificar al solicitante (fire-and-forget — FB.10.18)
  const reqData = reqSnap.data();
  void sendRequestResultNotification({
    recipientEmail: (reqData.email as string) ?? '',
    recipientName: ((reqData.displayName as string) ?? '').split(' ')[0], // Solo nombre de pila
    result: 'approved',
    duration,
  });
}

// -----------------------------------------------------------------------------
// Reject Request
// -----------------------------------------------------------------------------

/**
 * Rechaza una solicitud de rol Editor.
 */
export async function rejectRequest(
  requestId: string,
  adminUid: string,
  reason?: string,
): Promise<void> {
  if (!isConfigured || !db) {
    throw new Error('Firebase no configurado');
  }

  const requestRef = doc(db, COLLECTIONS.EDITOR_REQUESTS, requestId);

  // Leer datos del solicitante antes de actualizar (solo se rechaza una pendiente)
  const reqSnap = await getDoc(requestRef);
  if (!reqSnap.exists()) {
    throw new Error('Solicitud no encontrada');
  }
  const reqData = reqSnap.data();
  if (reqData.status !== 'pending') {
    throw new Error('La solicitud ya está resuelta');
  }

  await updateDoc(requestRef, {
    status: 'rejected',
    reviewedBy: adminUid,
    reviewedAt: serverTimestamp(),
    ...(reason ? { rejectionReason: reason } : {}),
  });

  // Notificar al solicitante (fire-and-forget — FB.10.18)
  void sendRequestResultNotification({
    recipientEmail: (reqData.email as string) ?? '',
    recipientName: ((reqData.displayName as string) ?? '').split(' ')[0], // Solo nombre de pila
    result: 'rejected',
    rejectionReason: reason,
  });
}

// -----------------------------------------------------------------------------
// List Resolved Requests (admin/moderator — protegido por rules)
// -----------------------------------------------------------------------------

export async function listResolvedRequests(): Promise<EditorRequest[]> {
  if (!isConfigured || !db) return [];

  const q = query(
    collection(db, COLLECTIONS.EDITOR_REQUESTS),
    where('status', 'in', ['approved', 'rejected']),
    orderBy('reviewedAt', 'desc'),
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      uid: data.uid ?? '',
      email: data.email ?? '',
      displayName: data.displayName ?? '',
      reason: data.reason ?? '',
      requestedAt: toDate(data.requestedAt),
      status: data.status ?? 'approved',
      reviewedBy: (data.reviewedBy as string) ?? undefined,
      reviewedAt: data.reviewedAt ? toDate(data.reviewedAt) : undefined,
      grantedDuration: (data.grantedDuration as RoleDuration) ?? undefined,
      rejectionReason: (data.rejectionReason as string) ?? undefined,
    } satisfies EditorRequest;
  });
}
